/**
 * HeaderNavHandler - A class for handling functionality
 */
class HeaderNavHandler {
	/**
	 * Setup method
	 * Static method to set up the class on the page
	 *
	 * @returns {void}
	 */
	static setup() {
		const elements = document.querySelectorAll('#header');

		for (const element of elements) {
			const instance = new HeaderNavHandler(element);
			instance.init();
		}
	}

	/**
	 * Constructor method
	 *
	 * @param {HTMLElement} element
	 * @returns {void}
	 */
	constructor(element) {
		this.header = element;
		this.body = document.body;
		this.mainHeader = this.header.querySelector('#main-header');
		this.mobileMenu = this.header.querySelector('#mobile-menu');
		this.mobileMenuToggle = this.header.querySelector('.mobile-toggle');
		this.headerHeight = this.header.offsetHeight;
		this.currentScrollPosition = window.scrollY;
		this.lastScrollPosition = this.currentScrollPosition;
		this.scrollThreshold = this.headerHeight * 0.3;
		this.navBreakpoint = window.matchMedia('(min-width: 992px)');
		this.searchButton = this.header.querySelector('.header-btn-search');
		this.searchDropdown = this.header.querySelector('.search-dropdown');
		this.searchInput = this.header.querySelector('.search-dropdown input[type="search"]');
	}

	/**
	 * Initialize method
	 * Method to initialize a class instance
	 */
	init() {
		this.updateHeaderHeight();
		this.handleScroll();
		this.addEventListeners();
	}

	/**
	 * Add event listeners
	 *
	 * @returns {void}
	 */
	addEventListeners() {
		window.addEventListener('resize', this.handleResize.bind(this));
		window.addEventListener('scroll', this.handleScroll.bind(this));

		document.addEventListener('keydown', this.handleKeyboardEvent.bind(this));
		document.addEventListener('click', this.handleClickOutside.bind(this));

		if (this.mobileMenuToggle) {
			this.mobileMenuToggle.addEventListener('click', this.toggleMobileMenu.bind(this));
		}

		if (this.searchButton) {
			this.searchButton.addEventListener('click', this.toggleSearchDropdown.bind(this));
		}
	}

	/**
	 * Toggle search dropdown
	 *
	 * @returns {void}
	 */
	toggleSearchDropdown() {
		if (!this.searchDropdown) return;

		const isActive = this.searchDropdown.classList.contains('active');

		this.closeMobileMenu();
		this.closeSubMenus();

		if (!isActive) {
			this.searchDropdown.classList.add('active');

			// Focus the search input
			if (this.searchInput) {
				// Slight delay ensures it's visible/rendered
				setTimeout(() => this.searchInput.focus(), 50);
			}
		} else {
			this.searchDropdown.classList.remove('active');
		}
	}

	/**
	 * Open the mobile menu
	 *
	 * @returns {void}
	 */
	openMobileMenu() {
		[this.mobileMenu, this.mobileMenuToggle, this.body].forEach((element) => element.classList.add('mobile-menu-open'));
		this.mobileMenu?.removeAttribute('inert');
		this.mobileMenu?.setAttribute('aria-hidden', 'false');
		this.updateHeaderHeight();
	}

	/**
	 * Close the mobile menu
	 *
	 * @returns {void}
	 */
	closeMobileMenu() {
		[this.mobileMenu, this.mobileMenuToggle, this.body].forEach((element) => element.classList.remove('mobile-menu-open'));
		this.mobileMenu?.setAttribute('inert', '');
		this.mobileMenu?.setAttribute('aria-hidden', 'true');

		// Close search dropdown if open
		if (this.searchDropdown) {
			this.searchDropdown.classList.remove('active');
		}

		this.handleDocumentEventListeners(false);
	}

	/**
	 * Toggle the mobile menu open/close state
	 *
	 * @returns {void}
	 */
	toggleMobileMenu() {
		this.body.classList.contains('mobile-menu-open') ? this.closeMobileMenu() : this.openMobileMenu();
	}

	/**
	 * Close all sub-menus
	 *
	 * @returns {void}
	 */
	closeSubMenus() {
		this.header.querySelectorAll('.open').forEach((subMenu) => subMenu.classList.remove('open'));

		// Close search dropdown if open
		if (this.searchDropdown) {
			this.searchDropdown.classList.remove('active');
		}
	}

	/**
	 * Handle document-wide event listeners
	 *
	 * @param {boolean} add - Whether to add or remove event listeners
	 * @returns {void}
	 */
	handleDocumentEventListeners(add) {
		if (add) {
			document.addEventListener('keydown', this.handleKeyboardEvent.bind(this));
			document.addEventListener('click', this.handleClickOutside.bind(this));
		} else {
			document.removeEventListener('keydown', this.handleKeyboardEvent.bind(this));
			document.removeEventListener('click', this.handleClickOutside.bind(this));
		}
	}

	/**
	 * Handle keyboard events
	 *
	 * @param {Event} event - The event object
	 * @returns {void}
	 */
	handleKeyboardEvent(event) {
		if (event.key === 'Escape') {
			if (this.searchDropdown) {
				this.searchDropdown.classList.remove('active');
			}

			// If mobile menu is open, close it and return focus
			if (this.body.classList.contains('mobile-menu-open')) {
				this.closeMobileMenu();
				this.mobileMenuToggle.focus();
			}
		}
	}

	/**
	 * Handle click outside of the header
	 *
	 * @param {Event} event - The event object
	 * @returns {void}
	 */
	handleClickOutside(event) {
		const isClickInsideSearch = event.target.closest('.header-btn-search, .search-dropdown');
		const isClickInsideMobile = event.target.closest('#mobile-menu, .mobile-toggle');

		if (!isClickInsideSearch && this.searchDropdown) {
			this.searchDropdown.classList.remove('active');
		}

		if (this.body.classList.contains('mobile-menu-open') && !isClickInsideMobile) {
			this.closeMobileMenu();
		}
	}

	/**
	 * Handle scroll event to manage sticky header
	 *
	 * @returns {void}
	 */
	handleScroll() {
		this.currentScrollPosition = window.scrollY;
		const isScrollingDown = this.currentScrollPosition > this.lastScrollPosition;
		const shouldBeSticky = this.currentScrollPosition > this.scrollThreshold;

		this.header.classList.toggle('nav-sticky', shouldBeSticky);
		if (shouldBeSticky) {
			this.header.classList.toggle('nav-up', isScrollingDown);
			this.header.classList.toggle('nav-down', !isScrollingDown);

			// Close sub-menus when nav is up
			if (isScrollingDown) {
				this.closeMobileMenu();
				this.closeSubMenus();
			}
		} else {
			this.header.classList.remove('nav-up', 'nav-down');
		}

		this.lastScrollPosition = this.currentScrollPosition;
	}

	/**
	 * Handle resize event to update header height
	 *
	 * @returns {void}
	 */
	handleResize() {
		this.updateHeaderHeight();

		if (this.navBreakpoint.matches) {
			this.closeMobileMenu();
		}
	}

	/**
	 * Update the height of the header and set a CSS variable
	 *
	 * @returns {void}
	 */
	updateHeaderHeight() {
		this.headerHeight = this.mainHeader.offsetHeight;
		this.body.style.setProperty('--header-height', `${this.headerHeight}px`);
	}
}

// The document is checked if it is ready. If it is, the setup() method is called.
if (document.readyState === 'interactive' || document.readyState === 'complete') {
	HeaderNavHandler.setup();
} else {
	document.addEventListener('DOMContentLoaded', HeaderNavHandler.setup);
}
